import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useRetryDispatch, useDispatchLogs } from '@/hooks/useDispatches';
import type { Dispatch, DispatchStatus } from '@/types/api.types';

const STATUS_LABEL: Record<DispatchStatus, string> = {
  pending: 'Pendente',
  sent: 'Enviado',
  delivered: 'Entregue',
  read: 'Lido',
  failed: 'Falhou',
};

const STATUS_VARIANT: Record<DispatchStatus, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  pending: 'outline',
  sent: 'secondary',
  delivered: 'default',
  read: 'default',
  failed: 'destructive',
};

function fmtDate(v: string | null) {
  if (!v) return '—';
  return format(new Date(v), "dd/MM/yyyy 'às' HH:mm:ss", { locale: ptBR });
}

interface Props {
  dispatch: Dispatch | null;
  onOpenChange: (open: boolean) => void;
}

const EVENT_LABEL: Record<string, string> = {
  SENT: 'Enviado para Zenvia',
  FAILED: 'Falha no envio',
  STATUS_SENT: 'Confirmado como enviado',
  STATUS_DELIVERED: 'Entregue ao destinatário',
  STATUS_READ: 'Lido pelo destinatário',
  STATUS_FAILED: 'Falha na entrega',
  STATUS_NOT_DELIVERED: 'Não entregue',
  STATUS_REJECTED: 'Rejeitado',
};

export function DispatchDetailDialog({ dispatch: d, onOpenChange }: Props) {
  const { mutate: retry, isPending } = useRetryDispatch();
  const { data: logs = [] } = useDispatchLogs(d?.id ?? null);

  return (
    <Dialog open={!!d} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Detalhes do disparo</DialogTitle>
        </DialogHeader>

        {d && (
          <div className="space-y-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="font-mono text-sm">{d.contactPhone}</p>
                {d.contactName && (
                  <p className="text-xs text-muted-foreground">{d.contactName}</p>
                )}
              </div>
              <Badge variant={STATUS_VARIANT[d.status]}>{STATUS_LABEL[d.status]}</Badge>
            </div>

            <div className="overflow-hidden rounded-md border text-xs">
              {[
                { label: 'Criado', value: d.createdAt },
                { label: 'Enviado', value: d.sentAt },
                { label: 'Entregue', value: d.deliveredAt },
                { label: 'Lido', value: d.readAt },
                { label: 'Falhou', value: d.failedAt },
              ].map(({ label, value }) => (
                <div key={label} className="flex justify-between border-b px-3 py-2 last:border-0">
                  <span className="text-muted-foreground">{label}</span>
                  <span className="font-mono">{fmtDate(value)}</span>
                </div>
              ))}
            </div>

            {d.errorMessage && (
              <div className="rounded-md bg-destructive/10 p-3 text-xs text-destructive">
                {d.errorMessage}
              </div>
            )}

            {d.zenviaMessageId && (
              <p className="text-xs text-muted-foreground">
                Zenvia ID:{' '}
                <span className="font-mono">{d.zenviaMessageId}</span>
              </p>
            )}

            <div>
              <p className="mb-1 text-xs font-medium text-muted-foreground">Payload</p>
              <pre className="max-h-48 overflow-auto rounded-md bg-muted p-3 text-xs">
                {JSON.stringify(d.payload, null, 2)}
              </pre>
            </div>

            {logs.length > 0 && (
              <div>
                <p className="mb-2 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Histórico de eventos
                </p>
                <ol className="relative border-l border-border space-y-3 pl-4">
                  {logs.map((log) => {
                    const p = log.payload as Record<string, unknown> | null;
                    const detail = p?.description ?? p?.error ?? null;
                    const isFailure = log.eventType.includes('FAILED') ||
                      log.eventType.includes('REJECTED') ||
                      log.eventType.includes('NOT_DELIVERED');

                    return (
                      <li key={log.id} className="relative">
                        <span className={`absolute -left-[17px] top-1 h-2.5 w-2.5 rounded-full border-2 border-background ${isFailure ? 'bg-destructive' : 'bg-primary'}`} />
                        <div className="flex items-baseline justify-between gap-2">
                          <span className={`text-xs font-medium ${isFailure ? 'text-destructive' : ''}`}>
                            {EVENT_LABEL[log.eventType] ?? log.eventType}
                          </span>
                          <span className="shrink-0 text-[10px] text-muted-foreground font-mono">
                            {format(new Date(log.createdAt), 'dd/MM HH:mm:ss', { locale: ptBR })}
                          </span>
                        </div>
                        {detail && (
                          <p className={`mt-0.5 text-[11px] ${isFailure ? 'text-destructive/80' : 'text-muted-foreground'}`}>
                            {String(detail)}
                          </p>
                        )}
                      </li>
                    );
                  })}
                </ol>
              </div>
            )}

            {d.status === 'failed' && (
              <Button
                variant="outline"
                className="w-full"
                disabled={isPending}
                onClick={() =>
                  retry(d.id, { onSuccess: () => onOpenChange(false) })
                }
              >
                {isPending ? 'Reenviando…' : 'Reenviar'}
              </Button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
